import { FunctionComponent, memo } from "react";
import { SpinnerUntil, Tooltip } from "@edgetier/components";
import { DetailsSection, DetailsSectionTitle } from "@edgetier/client-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons";

import InteractionMetricsDetails from "./interaction-metrics-details";
import { useInteractionMetrics } from "./interaction-metrics.utilities";
import { IProps } from "./interaction-metrics.types";
import "./interaction-metrics.scss";

/**
 * Display the metrics relating to an interaction.
 * @param props.interactionId The ID of the interaction that the metrics belong to.
 */
const InteractionMetrics: FunctionComponent<IProps> = ({ interactionId }) => {
    const { data: interactionMetrics, isLoading } = useInteractionMetrics(interactionId);

    return (
        <div className="interaction-metrics">
            <SpinnerUntil data={interactionMetrics} isReady={!isLoading}>
                {typeof interactionMetrics === "undefined" && (
                    <DetailsSection>
                        <DetailsSectionTitle title="Interaction Metrics" />
                        <div className="interaction-metrics__error-message">
                            No metrics found
                            <Tooltip content="If this interaction is still in progress, metrics may be added later.">
                                <FontAwesomeIcon icon={faInfoCircle} />
                            </Tooltip>
                        </div>
                    </DetailsSection>
                )}
                {Array.isArray(interactionMetrics) && interactionMetrics.length > 0 && (
                    <DetailsSection>
                        <DetailsSectionTitle title="Interaction Metrics" />
                        <div className="details-fields">
                            <table aria-label="interaction-metrics">
                                <tbody>
                                    {interactionMetrics.map(({ title, value }) => (
                                        <InteractionMetricsDetails
                                            key={title}
                                            interactionMetric={title}
                                            value={value}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </DetailsSection>
                )}
            </SpinnerUntil>
        </div>
    );
};

export default memo(InteractionMetrics);
